import { AfterViewInit, Component, Input } from '@angular/core';
import { ChartModel } from '../../api/models/chart/chart-model';
import { v4 as uuidv4 } from 'uuid';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ChartConfiguration } from 'chart.js';
import { ChartType } from '../../api/models/chart/chart-type';

Chart.defaults.font.family = 'Inter';

/**
 * Model which uses the ChartJS package to render a chart with the specified configuration
 */
@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  standalone: false,
})
export class ChartComponent implements AfterViewInit {
  darkMode =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  /**
   * The chart to be rendered
   */
  @Input({ required: true }) chart!: ChartModel;

  /**
   * Random UUID assigned to this chart, so that if rendering instances of this component on the same page, each one
   * can be identified in the DOM by its respective component
   */
  chartId = uuidv4();

  /**
   * The dataset colours to use
   */
  colours = [
    '#C4B5FD',
    '#93C5FD',
    '#A7F3D0',
    '#FDE68A',
    '#F9A8D4',
    '#FDBA74',
    '#F87171',
  ];

  ngAfterViewInit(): void {
    this.setCanvas();
  }

  /**
   * Updates the canvas based on the current chart configuration
   */
  setCanvas() {
    const chartConfig: ChartConfiguration = {
      type: this.chart.chartType.toString().toLowerCase() as ChartType,
      data: {
        labels: this.chart.labels,
        datasets: this.chart.datasets,
      },
      plugins: [ChartDataLabels],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: this.shouldDisplayLegend(),
            position: 'right',
            labels: {
              usePointStyle: true,
              font: {
                size: 14,
              },
              color: 'var(--inverted-neutral)',
            },
          },
          datalabels: {
            display: this.useDataLabels(),
            color: this.darkMode ? 'white' : 'black',
            font: {
              size: 13,
            },
          },
        },
        scales: {
          x: {
            display: this.shouldDisplayScales(),
            grid: {
              display: false,
            },
            ticks: {
              color: this.darkMode ? 'white' : 'black',
            },
          },
          y: {
            display: this.shouldDisplayScales(),
            grid: {
              display: false,
            },
            ticks: {
              color: this.darkMode ? 'white' : 'black',
            },
          },
        },
      },
    };

    let index = 0;
    // Set custom colours for datasets
    for (const dataset of chartConfig.data.datasets) {
      const colour = this.colours[index % this.colours.length];
      if (
        this.chart.chartType.toLowerCase() === ChartType.Bar.toLowerCase() ||
        this.chart.chartType.toLowerCase() === ChartType.Pie.toLowerCase()
      ) {
        dataset.backgroundColor = this.colours;
      } else {
        // Line chart: solid border, translucent fill
        dataset.borderColor = colour;
        dataset.backgroundColor = colour + '33';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dataset.tension = 0.4;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dataset.pointRadius = 3;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dataset.pointHoverRadius = 5;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dataset.borderWidth = 2;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dataset.borderRadius = this.getDatasetBorderRadius();

      index += 1;
    }

    new Chart(
      document.getElementById(this.chartId) as HTMLCanvasElement,
      chartConfig,
    );
  }

  /**
   * Returns true if the legend should be displayed for the chart
   */
  shouldDisplayLegend(): boolean {
    return !(
      this.chart.chartType.toLowerCase() == ChartType.Bar.toLowerCase() ||
      this.chart.chartType.toLowerCase() == ChartType.Line.toLowerCase()
    );
  }

  /**
   * Returns true if the x and y scales should be displayed
   */
  shouldDisplayScales(): boolean {
    return this.chart.chartType.toLowerCase() != ChartType.Pie.toLowerCase();
  }

  /**
   * Gets the border radius for the chart based on the type
   */
  getDatasetBorderRadius(): number {
    if (this.chart.chartType.toLowerCase() == ChartType.Bar.toLowerCase()) {
      return 8;
    }

    return 0;
  }

  /**
   * Returns true if data labels should be used
   */
  useDataLabels() {
    return this.chart.chartType.toLowerCase() != ChartType.Line.toLowerCase();
  }
}
